import React, { useState, useEffect } from "react";

import SelectData from "../../components/MeasurementsSteps/SelectData";
import ManageData from "../../components/MeasurementsSteps/ManageData";

import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { addKeyIfExists, mapLookupToSelect } from "../../utils/Helpers";
import { getCurrentDateYMD } from "../../utils/DateHelper";

import ScansAPI from "../../API/scans.api";
import LookupsAPI from "../../API/lookups.api";
import MeasurementsAPI from "../../API/measurements.api";
import { useTranslation } from "react-i18next";
import {
    getInitialDateFilter,
    getMappedFilters,
    getSessionFilters,
} from "../../utils/FilterHelpers";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";

const measurementsInitialFilters = () => {
    const measurementsLocalStorageFilters = getSessionFilters(
        "measurement-collect-filters"
    );

    return !isEmpty(measurementsLocalStorageFilters)
        ? measurementsLocalStorageFilters
        : getInitialDateFilter();
};

const scansInitialFilters = () => {
    const scansLocalStorageFilters = getSessionFilters(
        "measurement-scan-filters"
    );

    return !isEmpty(scansLocalStorageFilters)
        ? scansLocalStorageFilters
        : getInitialDateFilter("capturedAt");
};

const MEASUREMENTS_PER_REQUEST = 250;

const Measurements = () => {
    const history = useHistory();
    const [step, setStep] = useState(1);

    //Step 1
    const [materialsSelected, setMaterialsSelected] = useState([]);
    const [isCollect, setIsCollect] = useState(true);

    const [scanFilters, setScanFilters] = useState(() => scansInitialFilters());
    const [scanAppliedFilters, setScanAppliedFilters] = useState(() =>
        scansInitialFilters()
    );
    const [measurementFilters, setMeasurementFilters] = useState(() =>
        measurementsInitialFilters()
    );
    const [measurementAppliedFilters, setMeasurementAppliedFilters] = useState(
        () => {
            return measurementsInitialFilters();
        }
    );
    const [usersLookup, setUsersLookup] = useState([]);

    //Step 2
    const [data, setData] = useState(null);
    const { t } = useTranslation();

    const scansFilterOptions = [
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("createdBy"),
            name: "createdById",
            type: "Dropdown",
            options: usersLookup,
        },
        {
            label: t("capturedAt"),
            name: "capturedAt",
            type: "Date",
            monthBoundary: 3,
            max: getCurrentDateYMD(),
        },
    ];

    const measurmentsFilterOptions = [
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("createdBy"),
            name: "createdById",
            type: "Dropdown",
            options: usersLookup,
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            monthBoundary: 3,
            max: getCurrentDateYMD(),
        },
    ];

    useEffect(() => {
        fetchUsersLookup();
    }, []);

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const details = query.get("details");

        if (!details) {
            setStep(1);
        }
    }, [location.search]);

    const fetchUsersLookup = async () => {
        const result = await LookupsAPI.getUsers();
        setUsersLookup(mapLookupToSelect(result, "email"));
    };

    const incrementStep = () => {
        setStep(step + 1);
        history.push("/measurements?details=true");
    };

    const decrementStep = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const getCloudMeasurementData = async (specificData) => {
        let startIndex = 0,
            totalCount,
            totalData = [];
        if (!specificData.length) return totalData;
        const newFilters = getMappedFilters(measurementAppliedFilters);
        do {
            const res = await MeasurementsAPI.getMeasurementsDetails(
                MEASUREMENTS_PER_REQUEST,
                specificData,
                startIndex,
                newFilters
            );
            startIndex += 1;
            totalCount = res?.totalCount ? res.totalCount : 0;
            !isEmpty(res) && totalData.push(res);

            if (totalCount <= startIndex * MEASUREMENTS_PER_REQUEST) break;
        } while (true);
        return totalData;
    };

    const getCloudScansData = async (specificData) => {
        let startIndex = 0,
            totalCount,
            totalData = [];
        if (!specificData.length) return totalData;
        const newFilters = getMappedFilters(scanAppliedFilters);
        do {
            const res = await ScansAPI.getScanMeasurementsWithDetails(
                MEASUREMENTS_PER_REQUEST,
                specificData,
                startIndex,
                newFilters
            );

            startIndex += 1;
            totalCount = res?.totalCount ? res.totalCount : 0;
            totalData.push(res);

            if (totalCount <= startIndex * MEASUREMENTS_PER_REQUEST) break;
        } while (true);
        return totalData;
    };

    const applyCloudData = async (measurementData, scanData) => {
        try {
            const measurements = await getCloudMeasurementData(measurementData);
            const scans = await getCloudScansData(scanData);

            const totalData = [...measurements, ...scans];
            const headers = totalData[0].headers;
            let rows = [],
                ids = [],
                distinct = [],
                settings = [],
                metadata = {},
                measurementDetails = {};

            const DEFINED_METADATA = ["scanner", "createdAt", "createdBy"];

            for (const row of totalData) {
                const innerMeasurements = row.measurements || row.scans;
                settings = settings.concat(row["settings"]);
                for (const measurement of innerMeasurements) {
                    const dataRow = [
                        measurement.name,
                        ...measurement.spectralData,
                    ];
                    rows.push(dataRow);
                    ids.push({ id: measurement.id, key: measurement.key });
                    for (const key of DEFINED_METADATA) {
                        addKeyIfExists(measurement, key, metadata);
                    }

                    if (!distinct.includes(measurement.name)) {
                        distinct.push(measurement.name);
                    }
                    measurementDetails[
                        `${measurement.id}_${measurement.key || ""}`
                    ] = measurement;
                }
            }
            setData({ headers, rows, metadata, ids, measurementDetails });
            SuccessAlert(t("dataRetrievedSuccessfully"));
            return {
                headers,
                rows,
                metadata,
                ids,
                measurementDetails,
            };
        } catch (error) {
            ErrorAlert(
                t("dataRetrieveFail", {
                    error,
                })
            );
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <SelectData
                        incrementStep={incrementStep}
                        applyCloudData={applyCloudData}
                        selectedRows={materialsSelected}
                        setSelectedRows={setMaterialsSelected}
                        scansFilterOptions={scansFilterOptions}
                        measurmentsFilterOptions={measurmentsFilterOptions}
                        isCollect={isCollect}
                        setIsCollect={setIsCollect}
                        filters={isCollect ? measurementFilters : scanFilters}
                        setFilters={
                            isCollect ? setMeasurementFilters : setScanFilters
                        }
                        appliedFilters={
                            isCollect
                                ? measurementAppliedFilters
                                : scanAppliedFilters
                        }
                        setAppliedFilters={
                            isCollect
                                ? setMeasurementAppliedFilters
                                : setScanAppliedFilters
                        }
                        scanAppliedFilters={scanAppliedFilters}
                        measurementAppliedFilters={measurementAppliedFilters}
                    />
                );
            case 2:
                return (
                    <ManageData
                        data={data}
                        setData={setData}
                        decrementStep={decrementStep}
                        materialsSelected={materialsSelected}
                    />
                );
        }
    };
    return <>{renderStep()}</>;
};

export default Measurements;
